import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Check, ArrowRight, Crown } from "lucide-react";
import { api } from "../lib/api";
import { useCurrency } from "../context/CurrencyContext";

export const BundleOffer = () => {
  const { currency } = useCurrency();
  const [bundle, setBundle] = useState(null);

  useEffect(() => {
    api
      .get(`/products?currency=${currency}`)
      .then((res) => {
        const b = (res.data.products || []).find((p) => p.is_bundle);
        setBundle(b);
      })
      .catch(() => setBundle(null));
  }, [currency]);

  if (!bundle) return null;

  const savings = bundle.original_price - bundle.price;
  const savingsPct = Math.round((savings / bundle.original_price) * 100);

  return (
    <section
      id="bundle"
      data-testid="bundle-offer-section"
      className="relative bg-[#0A192F] overflow-hidden py-20 sm:py-28"
    >
      {/* Decorative grid + gold corner lines */}
      <div className="absolute inset-0 hero-grid opacity-25 pointer-events-none" />
      <div className="absolute top-8 left-8 w-24 h-24 border-t-2 border-l-2 border-[#C5A059]/40 pointer-events-none" />
      <div className="absolute bottom-8 right-8 w-24 h-24 border-b-2 border-r-2 border-[#C5A059]/40 pointer-events-none" />

      <div className="relative max-w-6xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-3 mb-5">
            <div className="gold-divider" />
            <span className="font-accent text-[#C5A059] text-xs">EXCLUSIVE OFFER</span>
            <div className="gold-divider" />
          </div>
          <h2
            data-testid="bundle-heading"
            className="font-display text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05]"
          >
            Own <span className="italic text-[#C5A059]">every blueprint.</span>
            <br />
            Save {savingsPct}%.
          </h2>
        </div>

        <div className="relative bg-white border border-[#C5A059]/40 shadow-2xl shadow-black/40">
          {/* Best Value badge */}
          <div
            data-testid="best-value-badge"
            className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#C5A059] text-[#0A192F] font-accent text-[11px] px-5 py-2 flex items-center gap-1.5"
          >
            <Crown className="w-3.5 h-3.5" />
            BEST VALUE
          </div>

          <div className="grid lg:grid-cols-5 gap-0">
            {/* Left: content */}
            <div className="lg:col-span-3 p-8 sm:p-12 lg:p-14 border-b lg:border-b-0 lg:border-r border-slate-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 bg-[#0A192F] flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-[#C5A059]" />
                </div>
                <span className="font-accent text-[10px] text-slate-400">
                  {bundle.category.toUpperCase()}
                </span>
              </div>

              <h3 className="font-display text-3xl sm:text-4xl text-[#0A192F] mb-2 leading-tight">
                {bundle.name}
              </h3>
              <p className="text-[#C5A059] italic mb-7">{bundle.tagline}</p>

              <p className="text-slate-600 leading-relaxed mb-8">
                {bundle.short_description} Save more than{" "}
                <b className="text-[#0A192F]">
                  {bundle.symbol}
                  {savings.toFixed(2)}
                </b>{" "}
                vs. buying each plan individually.
              </p>

              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 mb-2">
                {bundle.highlights.map((h, i) => (
                  <div
                    key={h}
                    data-testid={`bundle-highlight-${i}`}
                    className="flex items-start gap-2 text-sm text-slate-700"
                  >
                    <Check className="w-4 h-4 text-[#C5A059] flex-shrink-0 mt-0.5" />
                    <span>{h}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: pricing rail */}
            <div className="lg:col-span-2 p-8 sm:p-12 lg:p-14 bg-[#F8FAFC] flex flex-col justify-center">
              <div className="font-accent text-[10px] text-slate-400 mb-3">FOUNDER'S PRICE</div>
              <div className="flex items-baseline gap-3 mb-3">
                <span
                  data-testid="bundle-price"
                  className="font-display text-5xl sm:text-6xl text-[#0A192F]"
                >
                  {bundle.symbol}
                  {bundle.price.toFixed(2)}
                </span>
              </div>
              <div className="flex items-center gap-3 mb-1">
                <span className="text-slate-400 line-through text-lg">
                  {bundle.symbol}
                  {bundle.original_price.toFixed(2)}
                </span>
                <span
                  data-testid="bundle-savings-badge"
                  className="font-accent text-[10px] bg-[#0A192F] text-[#C5A059] px-2 py-1"
                >
                  SAVE {savingsPct}%
                </span>
              </div>
              <p className="text-xs text-slate-500 mb-8">
                One-time payment &middot; Instant ZIP download &middot; All 5 PDFs included
              </p>

              <Link
                to={`/checkout/${bundle.id}`}
                data-testid="bundle-cta-button"
                className="group bg-[#C5A059] hover:bg-[#D4AF37] text-[#0A192F] font-bold py-4 px-5 text-sm inline-flex items-center justify-center gap-2 transition shadow-lg"
              >
                CLAIM THE BUNDLE
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition" />
              </Link>

              <p className="text-[11px] text-center text-slate-400 mt-4">
                Instant ZIP download &middot; All sales final
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
